import React, { useState } from 'react';
import { Users, GraduationCap, BookOpen, Building2, Shield, FileText, Moon, Check, type LucideIcon } from 'lucide-react';

interface Account {
  email:    string;
  password: string;
  labelMs:  string;
  labelEn:  string;
  roleMs:   string;
  roleEn:   string;
  Icon:     LucideIcon;
  color:    string;
}

const ACCOUNTS: Account[] = [
  { email:'admin@demo.serps.bn',     password:'Demo@2026', labelMs:'Pentadbir Sekolah',   labelEn:'School Admin',       roleMs:'SMAS Sayyidina Ali',     roleEn:'SMAS Sayyidina Ali',     Icon:Users,         color:'#1D4ED8' },
  { email:'guru@demo.serps.bn',      password:'Demo@2026', labelMs:'Guru',                labelEn:'Teacher',            roleMs:'Kelas Tingkatan 4A',     roleEn:'Form 4A Class',          Icon:GraduationCap, color:'#059669' },
  { email:'ibubapa@demo.serps.bn',   password:'Demo@2026', labelMs:'Ibu Bapa / Penjaga',  labelEn:'Parent / Guardian',  roleMs:'2 orang anak berdaftar', roleEn:'2 children enrolled',    Icon:BookOpen,      color:'#D97706' },
  { email:'moe@demo.serps.bn',       password:'Demo@2026', labelMs:'Pegawai KPM',         labelEn:'MOE Officer',        roleMs:'Akses semua sekolah',    roleEn:'All schools access',     Icon:Building2,     color:'#7C3AED' },
  { email:'dpe@demo.serps.bn',       password:'Demo@2026', labelMs:'Pegawai DPE',         labelEn:'DPE Officer',        roleMs:'Sekolah Swasta',         roleEn:'Private Schools',        Icon:FileText,      color:'#DC2626' },
  { email:'mora@demo.serps.bn',      password:'Demo@2026', labelMs:'Pegawai MORA',        labelEn:'MORA Officer',       roleMs:'Sekolah Agama',          roleEn:'Religious Schools',      Icon:Moon,          color:'#0D9488' },
  { email:'superadmin@moe.edu.bn',   password:'Demo@2026', labelMs:'Pentadbir Super',     labelEn:'Super Admin',        roleMs:'Akses Penuh Sistem',     roleEn:'Full System Access',     Icon:Shield,        color:'#374151' },
];

interface Props {
  /** Called when user clicks a role — pre-fills the form; does NOT log in */
  onSelectRole: (email: string, password: string) => void;
  /** Currently pre-filled email (to show selected state) */
  selectedEmail?: string;
  loading:  boolean;
  lang?:    string;
}

export function QuickLoginPanel({ onSelectRole, selectedEmail, loading, lang = 'ms' }: Props) {
  return (
    <div
      style={{
        backgroundColor: '#FFFFFF',
        border:          '1.5px solid #E2E8F0',
        borderRadius:    '14px',
        overflow:        'hidden',
        boxShadow:       '0 2px 8px rgba(0,0,0,0.06)',
      }}
      role="region"
      aria-label={lang === 'en' ? 'Quick access by role' : 'Akses pantas mengikut peranan'}
    >
      {/* Header */}
      <div style={{ padding:'10px 16px', background:'linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)', borderBottom:'1px solid #E2E8F0', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <span style={{ fontSize:'11px', color:'#64748B', fontWeight:600, letterSpacing:'0.04em', textTransform:'uppercase' }}>
          {lang === 'en' ? 'Quick access by role' : 'Akses pantas mengikut peranan'}
        </span>
        <span style={{ fontSize:'10px', color:'#94A3B8' }}>
          {lang === 'en' ? 'Click to pre-fill credentials' : 'Klik untuk isi kelayakan'}
        </span>
      </div>

      {/* 2-column role grid */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr' }}>
        {ACCOUNTS.map((acc, i) => {
          const col          = i % 2;
          const isLast       = i >= ACCOUNTS.length - (ACCOUNTS.length % 2 === 0 ? 2 : 1);
          const borderRight  = col === 0 ? '1px solid #E2E8F0' : 'none';
          const borderBottom = !isLast ? '1px solid #E2E8F0' : 'none';
          const isSelected   = selectedEmail === acc.email;
          return (
            <RoleCard
              key={acc.email}
              acc={acc}
              lang={lang}
              disabled={loading}
              isSelected={isSelected}
              borderRight={borderRight}
              borderBottom={borderBottom}
              onClick={() => onSelectRole(acc.email, acc.password)}
            />
          );
        })}
      </div>
    </div>
  );
}

function RoleCard({ acc, lang, disabled, isSelected, borderRight, borderBottom, onClick }: {
  acc: Account; lang: string; disabled: boolean; isSelected: boolean;
  borderRight: string; borderBottom: string; onClick: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      title={lang === 'en' ? `Sign in as ${acc.labelEn}` : `Log masuk sebagai ${acc.labelMs}`}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        gap: '6px', padding: '14px 10px',
        backgroundColor: isSelected ? `${acc.color}12` : hovered ? `${acc.color}08` : '#FFFFFF',
        border: 'none', borderRight, borderBottom,
        outline: isSelected ? `2px solid ${acc.color}` : 'none',
        outlineOffset: '-2px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'all 120ms ease',
        textAlign: 'center', fontFamily: '"Noto Sans", sans-serif',
        position: 'relative',
      }}
    >
      {/* Selected checkmark */}
      {isSelected && (
        <div style={{ position:'absolute', top:'6px', right:'8px', width:'16px', height:'16px', borderRadius:'50%', background: acc.color, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <Check size={10} color="#FFFFFF" strokeWidth={3} />
        </div>
      )}

      <div style={{ width:'34px', height:'34px', borderRadius:'8px', backgroundColor:`${acc.color}18`, display:'flex', alignItems:'center', justifyContent:'center', transition:'transform 120ms', transform: hovered && !disabled ? 'scale(1.08)' : 'scale(1)' }}>
        <acc.Icon size={17} color={acc.color} strokeWidth={1.8} />
      </div>
      <div>
        <div style={{ fontSize:'11px', fontWeight:700, color:'#1A1A1A', lineHeight:1.3 }}>
          {lang === 'en' ? acc.labelEn : acc.labelMs}
        </div>
        <div style={{ fontSize:'10px', color:'#9CA3AF', marginTop:'2px' }}>
          {lang === 'en' ? acc.roleEn : acc.roleMs}
        </div>
      </div>
    </button>
  );
}
